import { DateTime } from 'luxon';

// Mirrors the backend AssignmentTimeRangeContainsTripTimeRange constraint
// message template prefix in backend/apps/trips/constraints.py. Stable enough
// to match for reorder/detection without parsing the rest of the string.
const SCHEDULE_EXPLANATION_PREFIX = "This trip's pickup";

export function isScheduleExplanation(explanation: string): boolean {
  return explanation.startsWith(SCHEDULE_EXPLANATION_PREFIX);
}

// True when the schedule violation appears in the explanation list. Drives
// the reschedule-modal entry point: rescheduling addresses the schedule
// problem and (typically) the clocked-in/out cascade that comes with it.
// Other orthogonal violations (prerequisites, capacity, payer mismatches)
// may still cause the post-reschedule assign to fail — that error surfaces
// as a toast so the dispatcher can react. This is preferable to falling
// back to the legacy override path, which the model-layer save() check
// (`Trip ... time out of assignment ... time range`) now rejects outright.
export function hasScheduleExplanation(explanations: string[]): boolean {
  return explanations.some(isScheduleExplanation);
}

export function reorderScheduleExplanationLast(
  explanations: string[]
): string[] {
  const schedule: string[] = [];
  const others: string[] = [];
  for (const e of explanations) {
    (isScheduleExplanation(e) ? schedule : others).push(e);
  }
  return [...others, ...schedule];
}

// True when the trip's duration is short enough to fit somewhere inside
// the crew's shift. False means no pickup time exists that would keep the
// whole trip inside the shift, so the reschedule modal can't recover the
// assignment and the crew should be hidden from the menu / drop should be
// silently rejected.
export function tripFitsInShift({
  pickupAt,
  dropoffAt,
  crewStart,
  crewEnd,
}: {
  pickupAt: string;
  dropoffAt: string;
  crewStart: string;
  crewEnd: string;
}): boolean {
  const pickup = DateTime.fromISO(pickupAt);
  const dropoff = DateTime.fromISO(dropoffAt);
  const start = DateTime.fromISO(crewStart);
  const end = DateTime.fromISO(crewEnd);
  return end.diff(start) >= dropoff.diff(pickup);
}

export function isCrewScheduleViolation({
  pickupAt,
  dropoffAt,
  crewStart,
  crewEnd,
}: {
  pickupAt: string;
  dropoffAt: string;
  crewStart: string;
  crewEnd: string;
}): boolean {
  const pickup = DateTime.fromISO(pickupAt);
  const dropoff = DateTime.fromISO(dropoffAt);
  const start = DateTime.fromISO(crewStart);
  const end = DateTime.fromISO(crewEnd);
  return pickup < start || dropoff > end;
}

// Returns the pickup time we want to prefill the reschedule form with, or
// null when no pickup time would keep the whole trip inside the shift
// (i.e. trip duration exceeds the shift length).
//
// Rule: choose the latest pickup that keeps the whole trip inside the crew's
// shift, capped at the original pickup so we never push later than the
// dispatcher intended. If the original pickup is before the shift starts,
// clamp to the shift start. Backend's schedule constraint checks dropoff
// against crew end too, so we subtract trip duration from crew end rather
// than the original "crew_end - 1 minute" rule (which looped on long trips).
//
// Returning null lets the caller bail out of the reschedule modal entirely
// — there is no recoverable pickup time, so the dispatcher should see the
// standard invalid-assignment modal with all violations listed instead of
// being prompted to enter a time that will fail validation again.
export function suggestedPickupTimeWithinCrewSchedule({
  pickupAt,
  dropoffAt,
  crewStart,
  crewEnd,
}: {
  pickupAt: string;
  dropoffAt: string;
  crewStart: string;
  crewEnd: string;
}): string | null {
  const pickup = DateTime.fromISO(pickupAt);
  const dropoff = DateTime.fromISO(dropoffAt);
  const start = DateTime.fromISO(crewStart);
  const end = DateTime.fromISO(crewEnd);
  const duration = dropoff.diff(pickup);

  if (end.diff(start) < duration) return null;

  if (pickup < start) {
    return start.toISO();
  }
  const latestValidPickup = end.minus(duration);
  return DateTime.min(latestValidPickup, pickup).toISO();
}
